class LookbookCards extends HTMLElement {
  constructor() {
    super();
    this.section = this.closest('.shopify-section');
    this.leftCard = this.querySelector('[data-lookbook-dir="left"]');
    this.rightCard = this.querySelector('[data-lookbook-dir="right"]');
    this.mobileBreakpoint = 1024;
    this.scrollDistance = window.innerHeight;
    this.ticking = false;
    this.wasMobile = null;
    this.focusLocked = false;
  }

  connectedCallback() {
    requestAnimationFrame(() => this.init());
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.boundOnScroll, { passive: true });
    window.removeEventListener('resize', this.boundOnResize);
    this.removeEventListener('focusin', this.boundOnFocusIn);
    this.removeEventListener('focusout', this.boundOnFocusOut);
    if (this.section) this.section.style.height = '';
  }

  init() {
    this.boundOnScroll = () => this.onScroll();
    this.boundOnResize = () => this.onResize();
    this.boundOnFocusIn = (event) => this.onFocusIn(event);
    this.boundOnFocusOut = (event) => this.onFocusOut(event);
    window.addEventListener('scroll', this.boundOnScroll, { passive: true });
    window.addEventListener('resize', this.boundOnResize);
    this.addEventListener('focusin', this.boundOnFocusIn);
    this.addEventListener('focusout', this.boundOnFocusOut);
    this.wasMobile = this.isMobile();

    if (this.wasMobile) {
      this.enableMobileMode();
    } else {
      this.setSectionHeight();
      this.update();
    }
  }

  isMobile() {
    return window.matchMedia(`(max-width: ${this.mobileBreakpoint}px)`).matches;
  }

  // Mobile uses CSS sticky + normal scroll — clear desktop JS state only
  enableMobileMode() {
    this.focusLocked = false;
    if (this.section) this.section.style.height = '';
    if (this.leftCard) this.leftCard.style.transform = '';
    if (this.rightCard) this.rightCard.style.transform = '';
  }

  onFocusIn(event) {
    if (this.isMobile()) return;
    // Only when focus enters from outside — ignore tabbing between cards
    if (event.relatedTarget && this.contains(event.relatedTarget)) return;
    this.focusLocked = true;
    this.completeAnimation();
  }

  onFocusOut(event) {
    if (this.isMobile()) return;
    // Still focusing something inside — keep end state
    if (event.relatedTarget && this.contains(event.relatedTarget)) return;
    this.focusLocked = false;
    this.update();
  }

  // Snap cards to animation end so focused content is on-screen
  completeAnimation() {
    if (!this.section) return;

    const rect = this.section.getBoundingClientRect();
    const progress = Math.max(0, Math.min(1, -rect.top / this.scrollDistance));

    if (progress < 1) {
      // Align scroll so progress = 1 (sectionTop = -scrollDistance)
      window.scrollBy({ top: rect.top + this.scrollDistance, behavior: 'auto' });
    }

    this.snapToEnd();
  }

  snapToEnd() {
    if (this.leftCard) this.leftCard.style.transform = 'translateX(0%)';
    if (this.rightCard) this.rightCard.style.transform = 'translateX(0%)';
  }

  onResize() {
    const mobile = this.isMobile();

    // Mode switch: desktop <-> mobile
    if (mobile !== this.wasMobile) {
      this.wasMobile = mobile;
      if (mobile) {
        this.enableMobileMode();
      } else {
        this.scrollDistance = window.innerHeight;
        this.setSectionHeight();
        this.update();
      }
      return;
    }

    // Same mode — desktop keeps original resize behavior
    if (!mobile) {
      this.scrollDistance = window.innerHeight;
      this.setSectionHeight();
    }
  }

  setSectionHeight() {
    if (!this.section) return;
    this.section.style.height = `${window.innerHeight + this.scrollDistance}px`;
  }

  onScroll() {
    if (this.isMobile()) return;
    if (this.ticking) return;
    this.ticking = true;
    requestAnimationFrame(() => {
      this.update();
      this.ticking = false;
    });
  }

  // Desktop only — original translateX logic (unchanged)
  update() {
    if (this.focusLocked) {
      this.snapToEnd();
      return;
    }

    const rect = this.section.getBoundingClientRect();
    const sectionTop = rect.top;
    // progress 0 when section top touches viewport top, 1 when scrolled scrollDistance past
    const progress = Math.max(0, Math.min(1, -sectionTop / this.scrollDistance));
    // Left: -100% -> 0, Right: 100% -> 0
    const leftX = -100 * (1 - progress);
    const rightX = 100 * (1 - progress);
    if (this.leftCard) this.leftCard.style.transform = `translateX(${leftX}%)`;
    if (this.rightCard) this.rightCard.style.transform = `translateX(${rightX}%)`;
  }
}

if (!customElements.get('lookbook-cards')) {
  customElements.define('lookbook-cards', LookbookCards);
}
